/**
 * Small wrapper around `Intl.RelativeTimeFormat` used for the admin
 * dashboard "updated" column and the editor's auto-save indicator. Keeps
 * one rounding / unit-selection policy in the codebase so every "n min ago"
 * string in the UI reads identically.
 *
 * Returns plain strings ("2 min ago", "just now"). Anything older than a
 * year falls back to a locale-aware short date, because saying "53 weeks
 * ago" is more noise than signal.
 */
const THRESHOLDS: Array<{ limitSec: number; divisor: number; unit: Intl.RelativeTimeFormatUnit }> =
  [
    { limitSec: 60, divisor: 1, unit: 'second' },
    { limitSec: 60 * 60, divisor: 60, unit: 'minute' },
    { limitSec: 60 * 60 * 24, divisor: 60 * 60, unit: 'hour' },
    { limitSec: 60 * 60 * 24 * 7, divisor: 60 * 60 * 24, unit: 'day' },
    { limitSec: 60 * 60 * 24 * 30, divisor: 60 * 60 * 24 * 7, unit: 'week' },
    { limitSec: 60 * 60 * 24 * 365, divisor: 60 * 60 * 24 * 30, unit: 'month' },
  ];

let cachedFormatter: Intl.RelativeTimeFormat | null = null;
function rtf(): Intl.RelativeTimeFormat {
  if (!cachedFormatter) {
    cachedFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto', style: 'short' });
  }
  return cachedFormatter;
}

/**
 * Render `iso` as a human-friendly relative timestamp. Accepts ISO-8601
 * strings or anything `Date` can parse; returns an empty string for bad
 * input so call sites can safely interpolate.
 */
export function formatRelativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const then = new Date(iso);
  const thenMs = then.getTime();
  if (Number.isNaN(thenMs)) return '';

  const diffSec = Math.round((thenMs - now.getTime()) / 1000);
  const absSec = Math.abs(diffSec);
  if (absSec < 10) return 'just now';

  for (const { limitSec, divisor, unit } of THRESHOLDS) {
    if (absSec < limitSec) {
      return rtf().format(Math.round(diffSec / divisor), unit);
    }
  }
  return then.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
